/** Shared Pacis quote PDF styling (motor, PA, travel). */

/* eslint-disable no-param-reassign */

export const PDF_COLORS = {
  blue: [30, 66, 118],
  cyan: [0, 174, 239],
  gray: [100, 116, 139],
  lightBlue: [235, 245, 255],
};

export const PDF_MARGIN = 14;

export function formatPdfCurrency(val) {
  return new Intl.NumberFormat('en-KE', { maximumFractionDigits: 0 }).format(Math.round(val || 0));
}

export function formatPdfDate(date = new Date()) {
  return date.toLocaleDateString('en-KE', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function createPdfLayout(doc) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = PDF_MARGIN;
  return {
    doc,
    pageW,
    pageH,
    margin,
    contentW: pageW - margin * 2,
    y: 10,
  };
}

export function addLogo(doc, margin, y) {
  try {
    doc.addImage('/pacis-logo-tagline.png', 'PNG', margin, y, 38, 14);
  } catch (e) {
    // Logo optional in test/offline environments
  }
}

export function renderCompanyHeader(doc, layout, { agentName = 'N/A' } = {}) {
  const { pageW, margin } = layout;
  let { y } = layout;
  const { blue, cyan, gray } = PDF_COLORS;

  addLogo(doc, margin, y);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text('Pacis Insurance Company Limited', pageW - margin, y + 4, { align: 'right' });
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text('Pacis Centre, 4th Floor, off Waiyaki Way  |  P.O BOX 1870-00200, Nairobi', pageW - margin, y + 8.5, { align: 'right' });
  doc.text('Email: info@paciskenya.com  |  Tel: +254 706 610 000', pageW - margin, y + 12.5, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...cyan);
  doc.text(`INTERMEDIARY: ${agentName}`, pageW - margin, y + 17, { align: 'right' });

  y += 20;
  doc.setDrawColor(...blue);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 5;

  return y;
}

export function renderDocumentTitle(doc, layout, title, y) {
  const { pageW, margin } = layout;
  const { blue, cyan, gray } = PDF_COLORS;

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text(title, margin, y);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text(`Date: ${formatPdfDate()}`, pageW - margin, y, { align: 'right' });
  y += 8;

  doc.setDrawColor(...cyan);
  doc.setLineWidth(0.4);
  doc.line(margin, y, pageW - margin, y);
  y += 7;

  return y;
}

export function renderInvoiceMeta(doc, layout, { invoiceNo, clientName, clientEmail }, y) {
  const { pageW, margin } = layout;
  const { blue, gray } = PDF_COLORS;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text('INVOICE NO.', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text(invoiceNo, margin + 28, y);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text('BILL TO:', pageW - margin - 60, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text(clientName || 'N/A', pageW - margin - 40, y);
  y += 5;

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text('ISSUED:', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text(formatPdfDate(), margin + 28, y);
  doc.text(clientEmail || 'N/A', pageW - margin - 40, y);
  y += 8;

  doc.setDrawColor(...PDF_COLORS.cyan);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
  y += 6;

  return y;
}

export function renderInfoBox(doc, x, y, width, height, title, rows) {
  const { blue, gray, lightBlue } = PDF_COLORS;

  doc.setFillColor(...lightBlue);
  doc.roundedRect(x, y, width, height, 2, 2, 'F');
  doc.setDrawColor(210, 225, 240);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, width, height, 2, 2, 'S');

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text(title, x + 3, y + 5);

  rows.forEach(([key, val], i) => {
    const ry = y + 11 + i * 6;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text(key, x + 3, ry);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...blue);
    const lines = doc.splitTextToSize(String(val || 'N/A'), width - 26);
    doc.text(lines[0], x + 22, ry);
  });
}

export function renderIntermediaryStrip(doc, layout, agentName, y) {
  const { margin, contentW } = layout;
  const { blue, cyan, gray } = PDF_COLORS;

  doc.setFillColor(235, 245, 255);
  doc.setDrawColor(210, 225, 240);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, contentW, 8, 2, 2, 'FD');
  doc.setFillColor(...cyan);
  doc.rect(margin, y, 3, 8, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text('BILLED VIA — INTERMEDIARY', margin + 6, y + 3.2);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text(`Name: ${agentName}`, margin + 6, y + 6.2);
  return y + 14;
}

export function renderTableHeader(doc, layout, leftLabel, rightLabel, y) {
  const { margin, contentW, pageW } = layout;
  const { blue, lightBlue } = PDF_COLORS;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text(leftLabel, margin, y);
  y += 3;
  doc.setDrawColor(...blue);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageW - margin, y);
  y += 4;

  doc.setFillColor(...lightBlue);
  doc.rect(margin, y, contentW, 7, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text('Item Description', margin + 3, y + 5);
  doc.text(rightLabel, pageW - margin - 3, y + 5, { align: 'right' });
  return y + 7;
}

export function renderTableRow(doc, layout, label, amount, y, { dim = false } = {}) {
  const { margin, pageW } = layout;
  const { blue, gray } = PDF_COLORS;

  doc.setDrawColor(235, 240, 248);
  doc.setLineWidth(0.2);
  doc.line(margin, y, pageW - margin, y);
  doc.setFontSize(dim ? 8 : 9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...(dim ? gray : blue));
  const labelLines = doc.splitTextToSize(label, layout.contentW - 50);
  doc.text(labelLines[0], margin + 3, y + 5);
  const amountText = typeof amount === 'number' ? formatPdfCurrency(amount) : String(amount);
  doc.text(amountText, pageW - margin - 3, y + 5, { align: 'right' });
  return y + 7;
}

export function renderGrandTotal(doc, layout, total, y, { subtitle } = {}) {
  const { margin, contentW, pageW } = layout;
  const { blue, gray, lightBlue } = PDF_COLORS;
  const boxH = subtitle ? 14 : 11;

  doc.setFillColor(...lightBlue);
  doc.rect(margin, y, contentW, boxH, 'F');
  doc.setDrawColor(...blue);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  doc.line(margin, y + boxH, pageW - margin, y + boxH);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text('GRAND TOTAL', margin + 3, y + 7.5);
  doc.text(`Ksh ${formatPdfCurrency(total)}`, pageW - margin - 3, y + 7.5, { align: 'right' });
  if (subtitle) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text(subtitle, pageW - margin - 3, y + 11.5, { align: 'right' });
  }
  return y + (subtitle ? 23 : 20);
}

export function renderBenefitsTable(doc, layout, benefits, y, { limitHeader = 'Limit (KES)' } = {}) {
  const { margin, contentW, pageW } = layout;
  const { blue, cyan, lightBlue } = PDF_COLORS;

  doc.setFillColor(...lightBlue);
  doc.setDrawColor(210, 225, 240);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, contentW, 8, 2, 2, 'FD');
  doc.setFillColor(...cyan);
  doc.rect(margin, y, 3, 8, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text('BENEFITS & LIMITS', margin + 6, y + 5.5);
  y += 13;

  doc.setFillColor(...lightBlue);
  doc.rect(margin, y, contentW, 6, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text('Benefit', margin + 3, y + 4.2);
  doc.text(limitHeader, pageW - margin - 3, y + 4.2, { align: 'right' });
  y += 6;

  benefits.forEach((b, i) => {
    if (i % 2 === 0) {
      doc.setFillColor(250, 252, 255);
      doc.rect(margin, y, contentW, 7, 'F');
    }
    doc.setDrawColor(235, 240, 248);
    doc.setLineWidth(0.2);
    doc.line(margin, y + 7, pageW - margin, y + 7);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...blue);
    doc.text(b.label, margin + 3, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...cyan);
    const limitText = typeof b.limit === 'number'
      ? formatPdfCurrency(b.limit)
      : String(b.limit || 'Included');
    doc.text(limitText, pageW - margin - 3, y + 5, { align: 'right' });
    y += 7;
  });

  return y + 6;
}

export function renderValidityAndFooter(doc, layout, y) {
  const { margin, contentW, pageW } = layout;
  const { blue, cyan, gray } = PDF_COLORS;

  doc.setFillColor(255, 251, 235);
  doc.setDrawColor(253, 230, 138);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, contentW, 9, 2, 2, 'FD');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(146, 64, 14);
  doc.text(
    'Quote is only valid for 30 days. Cover commences only upon receipt of full premium. This is a computer-generated invoice.',
    margin + 3,
    y + 5.8,
  );
  y += 14;

  doc.setFillColor(...cyan);
  doc.rect(margin, y, contentW, 1.2, 'F');
  y += 6;

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text('Pacis Insurance Company Limited is regulated by the Insurance Regulatory Authority of Kenya.', margin, y);
  y += 4;
  doc.text('This is a computer-generated document and does not require a signature.', margin, y);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blue);
  doc.text(`Quotation — ${formatPdfDate()}`, pageW - margin, y, { align: 'right' });

  return y;
}
